import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
    Row,
    Col,
    Card,
    Button,
    Badge,
    Spinner,
    Container
} from "react-bootstrap";
import {
    FaArrowLeft,
    FaFileAlt
} from "react-icons/fa";
import Layout from "../../hocs/Layout";
import { get_datos_contrato_erp } from "../../services/contratos";
import GestionArchivos from "../../components/modals/GestionArchivos";
import GestionFacturas from "../../components/modals/GestionFacturas";
import ContratosListActions from "../../components/contratos/ContratosListActions";
import VirtualBatteryCard from "../../components/cards/VirtualBatteryCard";
import PayerCard from "../../components/cards/PayerCard";
import ClientCard from "../../components/cards/ClientCard";
import CupsCard from "../../components/cards/CupsCard";
import EconomicDataCard from "../../components/cards/EconomicDataCard";
import ContractDataCard from "../../components/cards/ContractDataCard";
import "./ContratoDetail.css";

const ContratoDetail = ({ user }) => {
    const { contrato_id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const sectorType = queryParams.get('sector_type');
    queryParams.delete('sector_type');
    const listSearch = queryParams.toString();
    const [contrato, setContrato] = useState(null);
    const [loading, setLoading] = useState(true);

    // Modal states
    const [showGestionArchivos, setShowGestionArchivos] = useState(false);
    const [showGestionFacturas, setShowGestionFacturas] = useState(false);
    const [contratoActivo, setContratoActivo] = useState(null);

    useEffect(() => {
        const fetchContrato = async () => {
            setLoading(true);
            try {
                const data = await get_datos_contrato_erp(contrato_id, sectorType);
                setContrato(data);
            } catch (error) {
                console.error("Error loading contract:", error);
            } finally {
                setLoading(false);
            }
        };

        if (contrato_id) {
            fetchContrato();
        }
    }, [contrato_id, sectorType]);

    // Action handlers
    const openGestionArchivos = (id, name, sector_type) => {
        setContratoActivo({ id, name, sector_type });
        setShowGestionArchivos(true);
    };

    const openGestionFacturas = (id, name, sector_type) => {
        setContratoActivo({ id, name, sector_type });
        setShowGestionFacturas(true);
    };

    const sectorTypeBadge = (sector_type) => {
        return sector_type === "electrico" ? (
            <Badge pill bg="warning" text="dark">
                ELECTRICIDAD
            </Badge>
        ) : (
            <Badge pill bg="info" text="dark">
                GAS
            </Badge>
        );
    };

    if (loading) {
        return (
            <Layout lg={10} md={12}>
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Cargando contrato...</p>
                </div>
            </Layout>
        );
    }

    if (!contrato) {
        return (
            <Layout lg={10} md={12} title="Detalle de Contrato">
                <div className="text-center mt-5">
                    <h3>No se encontró el contrato</h3>
                    <Button onClick={() => navigate(`/contratos/contratos${listSearch ? `?${listSearch}` : ''}`)} className="mt-3">
                        Volver al listado
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="py-5 bg-light min-vh-100">
                <Container>
                    {/* Header */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <Button
                                variant="light"
                                className="rounded-circle shadow-sm p-2 d-flex align-items-center justify-content-center"
                                style={{ width: '40px', height: '40px' }}
                                onClick={() => navigate(`/contratos/contratos${listSearch ? `?${listSearch}` : ''}`)}
                            >
                                <FaArrowLeft className="text-secondary" />
                            </Button>
                            <div>
                                <h2 className="mb-1 fw-bold text-dark">Contrato {contrato.name}</h2>
                                <div className="d-flex align-items-center gap-2">
                                    {sectorTypeBadge(contrato.sector_type)}
                                    <Badge bg="primary" className="rounded-pill px-3">
                                        {contrato.status_label || contrato.state}
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        <div>
                            <ContratosListActions
                                contrato={contrato}
                                onGestionArchivos={openGestionArchivos}
                                onGestionFacturas={openGestionFacturas}
                            />
                        </div>
                    </div>

                    {/* Main Content */}
                    <Row className="g-4">
                        {/* Información Cliente */}
                        <Col lg={6}>
                            <ClientCard
                                cliente={{
                                    nombre: contrato.titular,
                                    vat: contrato.nif,
                                    email: contrato.titular_email,
                                    phone: contrato.titular_phone,
                                    mobile: contrato.titular_mobile
                                }}
                            />
                        </Col>

                        {/* Información del CUPS */}
                        <Col lg={6}>
                            <CupsCard
                                cupsData={{
                                    code: contrato.cups,
                                    address: contrato.cups_direccion,
                                    city: contrato.cups_poblacion,
                                    province: contrato.cups_provincia
                                }}
                            />
                        </Col>

                        {/* Información Técnica y Económica */}
                        <Col lg={6}>
                            <EconomicDataCard
                                economicData={{
                                    tarifa: contrato.tarifa,
                                    tarifa_comercial: contrato.price_list,
                                    cnae: contrato.cnae,
                                    consumos: {
                                        calculado: contrato.consumo_anual_calculado,
                                        previsto: contrato.consumo_anual_previsto
                                    }
                                }}
                            />
                        </Col>

                        {/* Fechas y Agente */}
                        <Col lg={6}>
                            <ContractDataCard
                                contractData={{
                                    create_date: contrato.create_date,
                                    start_date: contrato.data_alta,
                                    type: contrato.tipo_contrato,
                                    state: contrato.status_label || contrato.state,
                                    prescriptor: contrato.prescriptor,
                                    extra_dates: [
                                        { label: 'Fecha Firma', value: contrato.data_firma_contracte },
                                        { label: 'Fecha Baja', value: contrato.data_baixa }
                                    ]
                                }}
                            />
                        </Col>

                        {/* Información del Pagador (Si es diferente al titular) */}
                        {contrato.pagador_name && (
                            <Col lg={6}>
                                <PayerCard
                                    pagador={{
                                        name: contrato.pagador_name,
                                        vat: contrato.pagador_vat,
                                        email: contrato.pagador_email,
                                        phone: contrato.pagador_phone
                                    }}
                                />
                            </Col>
                        )}

                        {/* Batería Virtual */}
                        <Col lg={6}>
                            <VirtualBatteryCard bateriaVirtual={contrato.bateria_virtual} />
                        </Col>

                        {/* Observaciones */}
                        {contrato.observacions && (
                            <Col lg={12}>
                                <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden">
                                    <Card.Header className="bg-white border-bottom-0 pt-4 px-4 d-flex align-items-center">
                                        <FaFileAlt className="me-2" />
                                        Observaciones
                                    </Card.Header>
                                    <Card.Body className="px-4 pb-4">
                                        <p className="text-muted mb-0">{contrato.observacions}</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </Container>
            </div>

            {/* Modals */}
            <GestionArchivos
                show={showGestionArchivos}
                close={setShowGestionArchivos}
                contrato={contratoActivo}
            />
            <GestionFacturas
                show={showGestionFacturas}
                close={setShowGestionFacturas}
                contrato={contratoActivo}
            />
        </Layout>
    );
};

const mapStateToProps = (state) => ({
    user: state.Auth.user,
    isAuthenticated: state.Auth.isAuthenticated,
});

export default connect(mapStateToProps)(ContratoDetail);
